import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Clock, MessageCircle, ShieldCheck, Sparkles, Star } from "lucide-react";
import { FORM_URL, IMAGES, WHATSAPP_MSG_URL } from "./data";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 16 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-b from-sapphire-light via-white to-white pt-28 md:pt-36"
    >
      <div className="hero-grid pointer-events-none absolute inset-0 opacity-70" />
      <div className="pointer-events-none absolute -top-24 right-[-8%] h-96 w-96 rounded-full bg-sapphire/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:pb-28">
        <div>
          <motion.div
            {...fade(0.1)}
            data-testid="hero-google-rating-badge"
            className="inline-flex flex-wrap items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm"
          >
            <span className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
              ))}
            </span>
            <span className="text-sm font-semibold text-slate-700">
              4.9 Rating based on 5980+ Google Reviews
            </span>
          </motion.div>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.22}>Siliguri&rsquo;s Trusted</MaskedLine>
            <MaskedLine delay={0.34}>
              <span className="text-sapphire">Deep Cleaning</span> &amp;
            </MaskedLine>
            <MaskedLine delay={0.46}>Facility Experts</MaskedLine>
          </h1>

          <motion.p {...fade(0.62)} className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Complete residential, commercial, and post-construction sanitization tailored to your space.
          </motion.p>

          <motion.div {...fade(0.76)} className="mt-8 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-google-form-primary-button"
              href={FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full bg-sapphire px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-sapphire/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-sapphire-dark sm:text-base"
            >
              Book Online / Submit Inquiry
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              data-testid="hero-whatsapp-secondary-button"
              href={WHATSAPP_MSG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border-2 border-whatsapp bg-white px-7 py-3.5 text-sm font-bold text-whatsapp-dark transition-all duration-300 hover:-translate-y-0.5 hover:bg-whatsapp hover:text-white sm:text-base"
            >
              <MessageCircle className="h-4 w-4" />
              Chat on WhatsApp
            </a>
          </motion.div>

          <motion.ul {...fade(0.9)} className="mt-12 flex flex-wrap gap-x-10 gap-y-4">
            {[
              ["500+", "Spaces transformed"],
              ["4.9★", "Google rating"],
              ["7 days", "Open every week"],
            ].map(([v, l]) => (
              <li key={l}>
                <p className="text-2xl font-extrabold tracking-tight text-slate-900">{v}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{l}</p>
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          style={{ y: parallaxY }}
          className="relative [perspective:1200px]"
          onMouseMove={handleMouse}
          onMouseLeave={() => {
            mx.set(0.5);
            my.set(0.5);
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 44, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="spotlight-frame relative overflow-hidden rounded-[2rem] border border-white/70"
          >
            <img
              src={IMAGES.hero}
              alt="swaclean professional deep cleaning a Siliguri home"
              className="h-[400px] w-full object-cover sm:h-[500px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sapphire-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70">Certified Crew</p>
                <p className="text-lg font-bold text-white">Deep clean in progress</p>
              </div>
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md">
                <ShieldCheck className="h-5 w-5" />
              </span>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
            className="absolute -left-3 top-8 flex items-center gap-3 rounded-2xl border border-white/60 bg-white/85 px-4 py-3 shadow-xl backdrop-blur-md sm:-left-8"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sapphire text-white">
              <Sparkles className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-sm font-bold text-slate-900">Spotless Finish</span>
              <span className="block text-xs font-medium text-slate-500">40-point checklist, every visit</span>
            </span>
          </motion.div>

          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 0.6 }}
            data-testid="hero-open-status-card"
            className="absolute -bottom-6 right-4 flex items-center gap-3 rounded-2xl border border-white/60 bg-white/90 px-4 py-3 shadow-xl backdrop-blur-md sm:right-8"
          >
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-whatsapp opacity-60" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-whatsapp" />
            </span>
            <span>
              <span className="block text-sm font-bold text-slate-900">Open Now</span>
              <span className="flex items-center gap-1 text-xs font-medium text-slate-500">
                <Clock className="h-3 w-3" /> Closes 7:00 PM · All week
              </span>
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
